import * as Sentry from "@sentry/react-native";
import { useRouter } from "expo-router";

import { useTranslation } from "@luna-restaurant/i18n";
import { ThemedHeader } from "@luna-restaurant/ui";

import { SchoolAuthenticatedHeader } from "@/components/SchoolAuthenticatedHeader";
import { useCurrentSchool } from "@/hooks/useCurrentSchool";
import { useAuthApi } from "@/services/authApi";

const LunaLogo = require("@/assets/images/luna-logo.png");

type ThemedHeaderContainerProps = {
  language: "de";
  activeNavigation?: string;
  displayNavigation?: boolean;
  variant?: "login" | "authenticated";
  onCustomAction?: () => void;
  customActionLabel?: string;
};

const LoginHeader = ({
  onCustomAction,
  customActionLabel,
}: Pick<
  ThemedHeaderContainerProps,
  "onCustomAction" | "customActionLabel"
>) => {
  const { t } = useTranslation();

  return (
    <ThemedHeader
      logo={LunaLogo}
      variant="login"
      showNavigation={false}
      customAction={
        onCustomAction
          ? {
              label: t(customActionLabel ?? "header.mensaApp"),
              onPress: onCustomAction,
              iconName: "chevron-left",
              variant: "login",
            }
          : undefined
      }
    />
  );
};

const AuthenticatedHeader = ({
  activeNavigation = "speiseplan",
  displayNavigation = true,
  language,
}: Pick<
  ThemedHeaderContainerProps,
  "activeNavigation" | "displayNavigation" | "language"
>) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { logout } = useAuthApi();
  const { currentSchool, isLoadingSchool } = useCurrentSchool();

  const currentSchoolLoading = isLoadingSchool && !currentSchool;

  const navigationItems = [
    {
      key: "speiseplan",
      label: t("header.mealPlan"),
      href: `/${language}/(auth)/speiseplan`,
      isActive: activeNavigation === "speiseplan",
    },
    {
      key: "schliesszeiten",
      label: t("header.closingTimes"),
      href: `/${language}/(auth)/schliesszeiten`,
      isActive: activeNavigation === "schliesszeiten",
    },
    {
      key: "gastbestellungen",
      label: t("header.guestOrders"),
      href: `/${language}/(auth)/gastbestellungen`,
      isActive: activeNavigation === "gastbestellungen",
    },
    {
      key: "lunchpakete",
      label: t("header.lunchBoxes"),
      href: `/${language}/(auth)/lunchpakete`,
      isActive: activeNavigation === "lunchpakete",
    },
    {
      key: "aktuelle-listen",
      label: t("header.currentLists"),
      href: `/${language}/(auth)/aktuelle-listen`,
      isActive: activeNavigation === "aktuelle-listen",
    },
  ] as const;

  const handleLogout = async () => {
    try {
      await logout();
      router.replace("/");
    } catch (error) {
      Sentry.captureException(error);
      console.error("handleLogout failed:", error);
    }
  };

  return (
    <SchoolAuthenticatedHeader
      logo={LunaLogo}
      navigationItems={displayNavigation ? navigationItems : []}
      currentSchoolLoading={currentSchoolLoading}
      currentSchool={
        currentSchool
          ? {
              name: currentSchool.name,
              abbreviation: currentSchool.abbreviation ?? undefined,
            }
          : undefined
      }
      onLogout={handleLogout}
      logoutLabel={t("header.logout")}
      showNavigation={displayNavigation}
      burgerBreakpoint={1400}
    />
  );
};

export const ThemedHeaderContainer = ({
  activeNavigation = "speiseplan",
  displayNavigation = true,
  variant = "authenticated",
  onCustomAction,
  customActionLabel,
  language,
}: ThemedHeaderContainerProps) => {
  if (variant === "login") {
    return (
      <LoginHeader
        onCustomAction={onCustomAction}
        customActionLabel={customActionLabel}
      />
    );
  }

  return (
    <AuthenticatedHeader
      activeNavigation={activeNavigation}
      displayNavigation={displayNavigation}
      language={language}
    />
  );
};
